/*
 *
 *  * Copyright(c) 2022 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import { Injectable } from '@angular/core';
import { DataTransformationService } from '@app/common/data-transformation.service';
import { DataValidationService } from '@app/util/data-validation.service';
import { InputFieldDef } from '@app/common/types/input-field-def';
import { SearchParamsError } from '@app/common/types/search-params-error';
import { AdvSearchFieldDef } from '@app/common/types/adv-search-field-def';
import { UtilService } from '@app/util/util.service';
import { AppConfigService } from '@app/common/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class TextValidatorService {
  private pattern = /(<|%3c|&lt)script|(^')|('$)|(--)|(';)/gi;
  private readonly ERROR_MSG = {
    specialCharError: 'advancedSearch.error.invalidInputCharacters',
    notSupportedCharSequenceError: 'advancedSearch.error.notSupportedCharSequence',
    charCountError: 'advancedSearch.error.insufficientChars',
    invalidScript: 'advancedSearch.error.invalidInputCharacters',
    charCountforSuffix: 'advancedSearch.validation.suffix',
    charCountforPrefix: 'advancedSearch.validation.prefix',
    nhi: 'advancedSearch.error.nhiRequiredChars'
  };

  constructor(
    private dataTransformationService: DataTransformationService,
    private dataValidationService: DataValidationService,
    private util: UtilService,
    private appConfig: AppConfigService
  ) {}

  validate(value: string, inputField: InputFieldDef) {
    if (inputField.inputType === 'nhi') {
      if (!UtilService.isEmpty(this.appConfig.getNHIPattern())) {
        return this.validateNHI(value);
      }
    } else {
      let errorMsg: string = this.notSupportedCharSequence(value, inputField);
      if (errorMsg) {
        return errorMsg;
      }
      errorMsg = this.checkCharCount(value, inputField);
      if (errorMsg) {
        return errorMsg;
      }
      errorMsg = this.invalidSearchInput(value, inputField);
      if (errorMsg) {
        return errorMsg;
      }
      errorMsg = this.containsSpecialChars(value, inputField);
      if (errorMsg) {
        return errorMsg;
      }
    }
  }

  validateNHI(value: string) {
    if (this.hasScriptingError(value)) {
      return this.ERROR_MSG.invalidScript;
    }

    let trimmed = '';
    let hasValue = false;
    if (value) {
      trimmed = value.trim();
      hasValue = true;
    }

    const charCount = trimmed.replace(/[-]|[ ]/g, '').length;
    if (charCount == 0) {
      return false;
    }

    if (
      hasValue &&
      !UtilService.isEmpty(this.appConfig.getNHIPattern()) &&
      !this.util.isValidNHI(trimmed, this.appConfig.getNHIPattern())
    ) {
      return this.ERROR_MSG.nhi;
    }
  }

  isValidInput = (key: string, worklistName, searchParamsError: SearchParamsError): boolean => {
    const errorMsg = this.validate(worklistName, AdvSearchFieldDef.get(key));
    searchParamsError[key] = errorMsg;
    return !errorMsg;
  };

  checkCharCount(value: string, inputField: InputFieldDef) {
    if (inputField.minChars) {
      let trimmed = '';
      // adding the variable hasValue  to handle the case when user type comma only like  ',,,,,,,'
      // the charCount in this case will be zero and we should show the error message
      let hasValue = false;

      if (value) {
        trimmed = value.trim();
        hasValue = true;
      }
      let charCount = trimmed.replace(/,/g, '').length;
      if (inputField.inputType === 'orderNumber' && trimmed[0] === '%' && trimmed.length > 1) {
        charCount = trimmed.substr(1).trim().length;
      }

      if (inputField.inputType === 'patientName') {
        const index = trimmed.indexOf(',');
        if (index > 0) {
          charCount = trimmed.length;
        } else if (index === 0) {
          charCount = 1;
        }
      }
      if (charCount >= 0 && hasValue && charCount < inputField.minChars) {
        return this.ERROR_MSG.charCountError;
      }
    }
  }

  invalidSearchInput(value: string, inputField: InputFieldDef) {
    const searchCriteriaTrim = value.trim().replace(/,/g, '');
    if (this.isInvalidPrefix(searchCriteriaTrim, inputField)) {
      return this.ERROR_MSG.charCountforPrefix;
    }
    if (this.isInvalidSuffix(searchCriteriaTrim, inputField)) {
      return this.ERROR_MSG.charCountforSuffix;
    }
  }

  isInvalidPrefix(value: string, inputField: InputFieldDef) {
    return this.dataValidationService.isInvalidPrefix(value, inputField.minPrefixChars) && inputField.allowPrefixSearch;
  }

  isInvalidSuffix(searchCriteria, inputField: InputFieldDef) {
    return (
      this.dataValidationService.isInvalidSuffix(searchCriteria, inputField.suffixChars) && inputField.allowSuffixSearch
    );
  }

  containsSpecialChars(value: string, inputField: InputFieldDef) {
    if (inputField.specialCharacters && value) {
      const notAllowedSuffixSearch = inputField.allowSuffixSearch === false;
      const notSupportedSequence =
        this.dataValidationService.hasInvalidWildcardsFormat(value, notAllowedSuffixSearch) ||
        (!this.isMRNOrAccessionSearch(inputField) && this.dataValidationService.hasSpecialChar(value));
      if (
        (inputField.specialCharacters instanceof RegExp &&
          !this.isMRNOrAccessionSearch(inputField) &&
          inputField.specialCharacters.test(value)) ||
        notSupportedSequence
      ) {
        return this.ERROR_MSG.specialCharError;
      }
    }
    if (this.hasScriptingError(value)) {
      return this.ERROR_MSG.invalidScript;
    }
  }

  hasScriptingError(value: string) {
    return value && value.trim().match(this.pattern);
  }

  isMRNOrAccessionSearch(inputField: InputFieldDef) {
    return inputField.inputType === 'accession' || inputField.inputType === 'mrn';
  }

  /**
   * Specifically used for NHI field
   * @param value - Input value of the textbox
   */
  formatInternational(value: string) {
    return this.dataTransformationService.getNonFormattedNHI(value.trim());
  }

  notSupportedCharSequence(value: string, inputField: InputFieldDef): string {
    let trimmed = '';
    const invalidChars = ['%', '-', '_'];
    if (value) {
      trimmed = value.trim();
    }
    if (inputField.inputType === 'orderNumber' && trimmed[0] === '%' && trimmed.length > 2) {
      if (trimmed[0] === '%' && trimmed.length > 2) {
        const inputStr = trimmed.substr(1, 2).trim();
        if (invalidChars.indexOf(inputStr[0]) > -1) {
          if (invalidChars.indexOf(inputStr[1]) > -1) {
            return this.ERROR_MSG.notSupportedCharSequenceError;
          }
        }
      }
    }
  }
}
